<template>
  <DialogWrapper v-if="showDialog">
    <template>
      <v-layout column class="booking-bulk-action-dialog">
        <v-layout justify-space-between class="title-container">
          <div class="dialog-title">{{dialogTitle}}</div>
          <v-btn class="close-button" text height="30px" width="30px" @click="closeDialog('close')"
                 :disabled="isLoading">
            <v-icon size="20">close</v-icon>
          </v-btn>
        </v-layout>
        <v-layout column class="content-container">
          <v-layout column v-if="isLoading">
            <v-img class="loading-image" src="images/booking_bulk_actions/loading.gif"/>
            <div class="loading-text">{{$t('booking.bulk_action_dialog.loading_message')}}</div>
          </v-layout>
          <div class="subtitle" v-show="!isLoading">
            {{dialogSubtitle}}
          </div>
          <v-layout class="checkbox-container"
                    v-if="action === 'cancel' && showRefundOption && !isLoading && !maxActionsExceeded"
                    align-content-start>
            <v-checkbox class="dialog-checkbox" v-model="refund" color="#3ac18f" hide-details/>
            <div class="dialog-text">{{$t('booking.bulk_action_dialog.refund')}}</div>
          </v-layout>
          <v-layout class="checkbox-container" v-if="possibleActions > 0 && !isLoading && !maxActionsExceeded"
                    align-content-start>
            <v-checkbox v-model="sendClientMessage"
                        class="dialog-checkbox shrink mr-0 mt-0" color="#3ac18f" hide-details/>
            <div class="dialog-text">{{$t('booking.bulk_action_dialog.client_message')}}</div>
          </v-layout>
          <v-layout v-if="sendClientMessage && !isLoading && !maxActionsExceeded" column>
            <div class="client-message-title">{{$t('booking.bulk_action_dialog.message')}}</div>
            <v-textarea outlined v-model="clientMessage" rows="3" class="client-message-text-area"/>
          </v-layout>

          <v-layout v-if="displayNoShowWarning" align-content-start>
            <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
            <div class="dialog-text">{{$t('booking.bulk_action_dialog.no_show_warning')}}</div>
          </v-layout>

          <v-layout v-if="action === 'cancel' && showPaymentWarning && !isLoading && !maxActionsExceeded"
                    align-content-start>
            <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
            <div class="dialog-text">{{$t('booking.bulk_action_dialog.payment_request_warning')}}</div>
          </v-layout>

          <v-layout class="bottom-item" align-content-start
                    v-if="action === 'accept' && showThreeTimeSlotsWarning && !isLoading && !maxActionsExceeded">
            <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
            <div class="dialog-text">{{$t('booking.bulk_action_dialog.three_time_slots')}}</div>
          </v-layout>
        </v-layout>
        <v-layout justify-end class="button-area">
          <v-btn text class="dialog-button" @click="closeDialog('cancel')" :disabled="isLoading">
            {{$t('booking.bulk_action_dialog.cancel')}}
          </v-btn>
          <v-btn v-if="possibleActions > 0 && !this.maxActionsExceeded" class="dialog-button" :color="brandColor"
                 elevation="0"
                 @click="closeDialog('confirm')"
                 :disabled="isLoading">
            <div class="confirm-button-label">{{$t('booking.bulk_action_dialog.confirm')}}</div>
          </v-btn>
        </v-layout>
      </v-layout>
    </template>
  </DialogWrapper>
</template>

<script>
  import DialogWrapper from "@/modules/common/components/dialog/DialogWrapper";

  export default {
    name: "BookingBulkActionDialog",
    components: {
      DialogWrapper,
    },
    props: {
      showDialog: {
        type: Boolean,
        required: true
      },
      action: {
        type: String,
        required: true,
        validator: prop => ['no-show', 'accept', 'cancel', ''].includes(prop)
      },
      maxActions: {
        type: Number,
        default: 100,
      },
      totalActions: {
        type: Number,
        required: true,
      },
      possibleActions: {
        type: Number,
        required: true,
      },
      showRefundOption: {
        type: Boolean,
        default: false,
      },
      showPaymentWarning: {
        type: Boolean,
        default: false,
      },
      showThreeTimeSlotsWarning: {
        type: Boolean,
        default: false,
      },
      initialMessageText: {
        type: String,
        default: '',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        refund: false,
        sendClientMessage: false,
        clientMessage: this.initialMessageText,
      };
    },
    computed: {
      maxActionsExceeded() {
        return this.possibleActions > this.maxActions;
      },
      dialogTitle() {
        if (this.maxActionsExceeded || this.possibleActions === 0) {
          return this.$t('booking.bulk_action_dialog.cannot_complete_action');
        }
        return this.$t('booking.bulk_action_dialog.title');
      },
      dialogSubtitle() {
        if (this.maxActionsExceeded) {
          return this.$t('booking.bulk_action_dialog.action_limit_exceeded', {max: this.maxActions});
        }

        if (this.possibleActions === 0) {
          return this.$t(`booking.bulk_action_dialog.cannot_complete_action_subtitle.${this.action}`);
        }
        return this.$t(`booking.bulk_action_dialog.subtitle.${this.action}`, {
          possible: '' + this.possibleActions,
          total: this.totalActions
        });
      },
      dialogState() {
        const state = {
          sendClientMessage: this.sendClientMessage,
          clientMessage: this.sendClientMessage ? this.clientMessage : '',
        };

        if (this.action === 'cancel') {
          state.refund = this.refund;
        }
        return state;
      },
      displayNoShowWarning() {
        return this.action === 'no-show' &&
          !this.isLoading &&
          this.possibleActions > 0 &&
          !this.maxActionsExceeded &&
          this.sendClientMessage;
      },
      brandColor() {
        return this.$store && this.$store.getters['brandModule/secondaryColor'];
      }
    },
    methods: {
      closeDialog(action) {
        const currentState = {...this.dialogState};
        this.refund = false;
        this.sendClientMessage = false;
        this.$emit(action, currentState);
      },
    },
    watch: {
      initialMessageText(value) {
        this.clientMessage = value;
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";
  @import "@/assets/scss/abstract/_i18n-mixins.scss";

  .loading-loader {
    height: 100px;
    margin: auto;
  }

  .booking-bulk-action-dialog {
    width: 100%;

    padding-bottom: 15px;
    background-color: $white-color-1;

    border-radius: 5px;
    box-shadow: 0 5px 24px 0 var(--2-gray-gray-400), 2px 13px 19px 2px rgba(0, 0, 0, 0.14), 0 7px 8px -4px var(--2-gray-gray-500);

    font-family: var(--primary-font-family);
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.87);

    @include md-and-up {
      width: 408px;
    }

    .title-container {
      border-bottom: 1px solid $dividerColor;
      @include i-padding-mirror(10px, 20px);
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      margin: 20px;
    }

    .close-button {
      border-radius: 1000px;
      min-width: unset;
      padding: 0;
      margin-top: auto;
      margin-bottom: auto;
    }

    .subtitle {
      padding-bottom: 20px;
      font-size: 15px;
      font-weight: 600;
      line-height: 1.33;
      letter-spacing: 0.21px;
    }

    .content-container {
      margin: 20px;
      padding: 0 10px;
    }

    .loading-image {
      width: 200px;
      margin: auto;
      min-height: 215px;
    }

    .loading-text {
      font-size: 15px;
      font-weight: 600;
      line-height: 1.33;
      color: #383838;
      margin: auto;
    }

    .dialog-text {
      align-self: center;
      font-size: 14px;
      line-height: 1.43;
      letter-spacing: 0.2px;
    }

    .checkbox-container {
      margin-left: -3px;
      padding: 8px 0;
    }

    .dialog-checkbox {
      margin-top: 0;
      padding-top: 0;
    }

    .client-message-title {
      margin-bottom: 4px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.54);
    }

    .client-message-text-area {
      font-size: 14px;
    }

    .warning-icon {
      @include i-padding-mirror(0, 14px);
      max-height: 20px;
    }

    .button-area {
      border-top: 1px solid $dividerColor;
      @include i-padding(15px, 15px, 0, 15px);

      .dialog-button {
        text-transform: initial;
        letter-spacing: normal;
        height: 40px;

        .confirm-button-label {
          color: white;
        }
      }
    }
  }
</style>
